import { formatSydneyDate } from '../utils/sydneyDateTime'
/** Shared helpers so profile header, risk card, and compliance sections agree. */

export function formatRiskScoreDisplay(score) {
  const n = Number(score)
  if (!Number.isFinite(n)) return '—'
  return Math.abs(n % 1) < 0.05 ? String(Math.round(n)) : n.toFixed(1)
}

export function resolveEffectiveRisk(profileData, patronProfile) {
  const rawScore = patronProfile?.risk_score ?? profileData?.RiskScore
  const score =
    rawScore == null || rawScore === '' ? null : Number(rawScore)
  const level = patronProfile?.risk_level ?? profileData?.RiskLevel ?? null
  return {
    score: Number.isFinite(score) ? score : null,
    level: level ? String(level) : null,
  }
}

export function parseRiskFactorsList(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

function factorText(f) {
  return `${f?.factor || ''} ${f?.detail || ''}`.trim()
}

/** True when saved risk_factors no longer match live compliance data on the page. */
export function riskAssessmentLooksStale(profileData, patronProfile, { amlChecks = [], kycEntries = [] } = {}) {
  const assessedAt = profileData?.RiskAssessedAt || patronProfile?.risk_assessed_at
  const factors = parseRiskFactorsList(profileData?.RiskFactors)

  if (factors.some((f) => /never screened/i.test(factorText(f))) && amlChecks.length > 0) {
    return true
  }
  if (factors.some((f) => /FATF High-Risk Country/i.test(f?.factor || ''))) {
    return true
  }

  const assessedTs = assessedAt ? new Date(assessedAt).getTime() : 0
  if (!assessedTs) return false

  for (const c of amlChecks) {
    const ts = c?.created_at ? new Date(c.created_at).getTime() : 0
    if (ts > assessedTs) return true
  }
  for (const k of kycEntries) {
    const ts = new Date(k?.performed_at || k?.created_at || 0).getTime()
    if (ts > assessedTs) return true
  }
  return false
}

/**
 * AML header should reflect material hits, not only the latest chronological row
 * (a later clear screen does not erase a confirmed adverse-media match).
 */
export function resolveAmlHeaderStatus(amlChecks) {
  if (!amlChecks?.length) {
    return { status: null, hint: 'No AML/PEP screening on record' }
  }

  const sorted = [...amlChecks].sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  )
  const latest = sorted[0]
  const confirmed = sorted.find((c) => String(c.analyst_decision || '').toLowerCase() === 'confirmed_match')
  const pendingHit = sorted.find(
    (c) =>
      Number(c.result_count || 0) > 0 &&
      String(c.analyst_decision || '').toLowerCase() !== 'no_match'
  )

  const material = confirmed || pendingHit
  if (material) {
    const status =
      material.aml_derived_category ||
      (material.analyst_decision === 'confirmed_match' ? 'Confirmed match' : 'Review required')
    const when = material.created_at
      ? formatSydneyDate(new Date(material.created_at))
      : null
    const latestClear =
      latest.analyst_decision === 'no_match' || Number(latest.result_count || 0) === 0
    const hint = latestClear && when
      ? `Latest screen clear · ${status} confirmed ${when}`
      : material.analyst_reason || material.reason || null
    return { status, hint }
  }

  if (latest.analyst_decision === 'no_match' || Number(latest.result_count || 0) === 0) {
    return { status: 'CLEAR', hint: latest.reason || 'Latest screen: no match' }
  }

  return {
    status: latest.aml_derived_category || 'REVIEW',
    hint: `${latest.result_count || 0} result(s) — ${latest.reason || 'review required'}`,
  }
}

function normalizeNamePart(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function ocrNameFromRows(rows) {
  if (!Array.isArray(rows)) return { given: '', family: '' }
  const get = (label) => {
    const row = rows.find((r) => String(r?.Name || r?.name || '').toLowerCase() === label.toLowerCase())
    return String(row?.Content ?? row?.content ?? row?.value ?? '').trim()
  }
  const given = get('Given name') || get('given_name') || get('Given Name')
  const family = get('Family name') || get('family_name') || get('Family Name')
  if (given || family) return { given, family }
  const flat = rows[0]
  if (flat && typeof flat === 'object' && (flat.given_name || flat.family_name)) {
    return {
      given: String(flat.given_name || '').trim(),
      family: String(flat.family_name || '').trim(),
    }
  }
  return { given: '', family: '' }
}

/** Returns warning copy when onboarding OCR names disagree with the profile record. */
export function detectOcrNameMismatch(profileData) {
  const profileGiven = normalizeNamePart(profileData?.FN)
  const profileFamily = normalizeNamePart(profileData?.LN)
  if (!profileGiven && !profileFamily) return null

  const sources = [
    { label: 'Profile creation document', rows: profileData?.OCRDetails },
    { label: 'DVS document', rows: profileData?.RecentOCRDetails },
  ]

  for (const src of sources) {
    const { given, family } = ocrNameFromRows(src.rows)
    if (!given && !family) continue
    const ocrGiven = normalizeNamePart(given)
    const ocrFamily = normalizeNamePart(family)
    const givenMismatch = ocrGiven && profileGiven && ocrGiven !== profileGiven
    const familyMismatch = ocrFamily && profileFamily && ocrFamily !== profileFamily
    if (givenMismatch || familyMismatch) {
      const ocrLabel = [given, family].filter(Boolean).join(' ')
      const profileLabel = [profileData?.FN, profileData?.LN].filter(Boolean).join(' ')
      return {
        source: src.label,
        ocrName: ocrLabel,
        profileName: profileLabel,
      }
    }
  }
  return null
}

function normalizeDobToken(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`
  }
  const parsed = new Date(raw)
  if (!Number.isNaN(parsed.getTime())) {
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')
    return `${parsed.getFullYear()}-${month}-${day}`
  }
  return raw.toLowerCase()
}

function ocrDobFromRows(rows) {
  if (!Array.isArray(rows)) return ''
  const labels = new Set(['date of birth', 'date_of_birth', 'dob', 'dateofbirth'])
  const row = rows.find((item) => {
    const name = String(item?.Name || item?.name || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
    return labels.has(name) || labels.has(name.replace(/ /g, ''))
  })
  const fromRow = row ? String(row.Content ?? row.content ?? row.value ?? '').trim() : ''
  if (fromRow) return fromRow
  const flat = rows[0]
  if (flat && typeof flat === 'object' && flat.date_of_birth) return String(flat.date_of_birth).trim()
  return ''
}

/** Returns warning copy when the licence OCR date of birth disagrees with the profile. */
export function detectOcrDobMismatch(profileData) {
  const profileDob = normalizeDobToken(profileData?.DOB)
  if (!profileDob) return null
  const sources = [
    { label: 'Profile creation document', rows: profileData?.OCRDetails },
    { label: 'DVS document', rows: profileData?.RecentOCRDetails },
  ]
  for (const src of sources) {
    const ocrDob = ocrDobFromRows(src.rows)
    const ocrToken = normalizeDobToken(ocrDob)
    if (!ocrToken || ocrToken === profileDob) continue
    return {
      source: src.label,
      ocrDob,
      profileDob: profileData.DOB,
    }
  }
  return null
}
